import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "./useAuth";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface SubscriptionStatus {
  isTrialActive: boolean;
  isPremium: boolean;
  daysRemaining: number;
  planType: 'free' | 'trial' | 'premium';
  trialEndsAt: Date | null;
  isLoading: boolean;
}

const defaultStatus: Omit<SubscriptionStatus, 'isLoading'> = {
  isTrialActive: false,
  isPremium: false,
  daysRemaining: 0,
  planType: 'free',
  trialEndsAt: null,
};

async function fetchSubscription(userId: string): Promise<Omit<SubscriptionStatus, 'isLoading'>> {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    return {
      ...defaultStatus,
      daysRemaining: 7, // Default trial days
    };
  }

  const now = new Date();
  const trialEnds = data.trial_ends_at ? new Date(data.trial_ends_at) : null;
  const premiumEnds = data.premium_ends_at ? new Date(data.premium_ends_at) : null;

  let isTrialActive = false;
  let isPremium = false;
  let daysRemaining = 0;

  if (data.plan_type === 'premium' && premiumEnds && premiumEnds > now) {
    isPremium = true;
    daysRemaining = Math.ceil((premiumEnds.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  } else if (data.plan_type === 'trial' && trialEnds && trialEnds > now) {
    isTrialActive = true;
    daysRemaining = Math.ceil((trialEnds.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  return {
    isTrialActive,
    isPremium,
    daysRemaining,
    planType: data.plan_type as 'free' | 'trial' | 'premium',
    trialEndsAt: trialEnds,
  };
}

export function useSubscriptionStatus(): SubscriptionStatus {
  const { data: user, isLoading: userLoading } = useAuthUser();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['subscription-status', user?.id],
    queryFn: () => fetchSubscription(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  // Realtime subscription for changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['subscription-status', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const isLoading = userLoading || query.isLoading;

  if (!user || query.error) {
    return { ...defaultStatus, isLoading };
  }

  return {
    ...(query.data ?? defaultStatus),
    isLoading,
  };
}
